/*
 * Main-thread proxy to an `encrypt.worker.ts` instance. One host per call.
 *
 * Lifecycle:
 *   - `new EncryptWorkerHost()`           — spawns the worker.
 *   - `host.init(...)` / `host.applyBlock(...)` / etc. — typed RPC.
 *   - `host.on('status', cb)`             — subscribe to async events.
 *   - `host.terminate()`                  — clean shutdown.
 *
 * The host never sees the call's private key: the worker generates it
 * (`createKey`) and only the public half comes back — keep that property as
 * the worker integration matures.
 */

import EventListenerBase from '@helpers/eventListenerBase';
// `?worker` follows the build's global `worker.format`, which is `es`
// (vite.config.ts) — so this is a MODULE worker, and the file name matters:
// `optimizeDeps.entries` scans `*.worker.ts` so the deps only it reaches
// (aes-js, libsodium-wrappers) are pre-bundled before the worker asks for them.
//
// NOTE: this import used to be documented as producing a classic (IIFE) worker,
// chosen because RTCRtpScriptTransform delivery to receivers looked flaky in
// Chrome with module workers. That was never what shipped — `worker.format` has
// been `es` since the vite migration, so a module worker is what has always run
// here, and switching would be a global change to every worker in the app.
import EncryptWorker from './encrypt.worker.ts?worker';
import type {
  HostRequest,
  HostResponse,
  RequestKind,
  RequestResultMap,
  WorkerEvent
} from './encryptWorkerProtocol';

type Pending = {resolve: (v: unknown) => void; reject: (e: Error) => void};

// Key destruction is best-effort, but thread termination is not. A wedged
// crypto operation ahead of `destroy` in the worker RPC queue must not retain
// the worker (and its key material) indefinitely.
const DESTROY_TIMEOUT_MS = 500;

type HostEvents = {
  status: (event: Extract<WorkerEvent, {kind: 'status'}>) => void;
  pendingOutbound: (event: Extract<WorkerEvent, {kind: 'pendingOutbound'}>) => void;
  callFailed: (event: Extract<WorkerEvent, {kind: 'callFailed'}>) => void;
  recvDiag: (event: Extract<WorkerEvent, {kind: 'recvDiag'}>) => void;
  [key: string]: (...args: any[]) => any;
};

export class EncryptWorkerHost extends EventListenerBase<HostEvents> {
  private readonly worker: Worker;
  private readonly pending = new Map<number, Pending>();
  private nextId = 1;
  private destroyed = false;
  private failed = false;
  private terminating = false;
  private terminationPromise: Promise<void> | undefined;

  constructor() {
    super(false);
    // Classic (IIFE) worker via Vite's `?worker` suffix. See the comment on
    // the import for why module workers were ditched.
    this.worker = new EncryptWorker({name: 'tde2e-encrypt'});
    this.worker.addEventListener('message', this.onMessage);
    this.worker.addEventListener('error', this.onError);
  }

  // ===== RPC methods =====
  //
  // One method per HostRequest kind. The TS signature mirrors the protocol's
  // `args` shape and the response type comes from `RequestResultMap`.

  /** Generate the call key inside the worker; only the public key comes back. */
  public createKey(): Promise<RequestResultMap['createKey']> {
    return this.invoke('createKey');
  }

  public createZeroBlock(args: Extract<HostRequest, {kind: 'createZeroBlock'}>['args']): Promise<RequestResultMap['createZeroBlock']> {
    return this.invoke('createZeroBlock', args);
  }

  public createSelfAddBlock(args: Extract<HostRequest, {kind: 'createSelfAddBlock'}>['args']): Promise<RequestResultMap['createSelfAddBlock']> {
    return this.invoke('createSelfAddBlock', args);
  }

  public init(args: Extract<HostRequest, {kind: 'init'}>['args']): Promise<RequestResultMap['init']> {
    return this.invoke('init', args);
  }

  /** Build and retain a rejoin block with the private key already in worker memory. */
  public prepareRejoinBlock(
    args: Extract<HostRequest, {kind: 'prepareRejoinBlock'}>['args']
  ): Promise<RequestResultMap['prepareRejoinBlock']> {
    return this.invoke('prepareRejoinBlock', args);
  }

  /** Re-anchor to the exact block returned by the latest prepareRejoinBlock call. */
  public commitRejoinBlock(): Promise<RequestResultMap['commitRejoinBlock']> {
    return this.invoke('commitRejoinBlock');
  }

  public applyBlock(args: Extract<HostRequest, {kind: 'applyBlock'}>['args']): Promise<RequestResultMap['applyBlock']> {
    return this.invoke('applyBlock', args);
  }

  public buildRemoveParticipantsBlock(
    args: Extract<HostRequest, {kind: 'buildRemoveParticipantsBlock'}>['args']
  ): Promise<RequestResultMap['buildRemoveParticipantsBlock']> {
    return this.invoke('buildRemoveParticipantsBlock', args);
  }

  public pullOutbound(): Promise<RequestResultMap['pullOutbound']> {
    return this.invoke('pullOutbound');
  }

  public receiveInbound(args: Extract<HostRequest, {kind: 'receiveInbound'}>['args']): Promise<RequestResultMap['receiveInbound']> {
    return this.invoke('receiveInbound', args);
  }

  public getStatus(): Promise<RequestResultMap['getStatus']> {
    return this.invoke('getStatus');
  }

  // Send `destroy` to the worker (wiping the key) then terminate it.
  public terminate(): Promise<void> {
    if(this.terminationPromise) return this.terminationPromise;
    if(this.destroyed) return Promise.resolve();

    this.terminating = true;
    this.terminationPromise = this.terminateInternal();
    return this.terminationPromise;
  }

  // Construct an `RTCRtpScriptTransform` attached to this worker. The caller
  // assigns the returned transform to an `RTCRtpSender.transform` (for the
  // 'send' direction) or `RTCRtpReceiver.transform` ('recv').
  //
  // - `channelId` is the e2e logical stream id (0-1023). Pick a stable value
  //   per (sender, media kind) — see notes/call.md.
  // - `unencryptedPrefixLength` keeps the leading N codec bytes in the clear
  //   (still authenticated). Defaults to 0; set to a small value (e.g. 1)
  //   for VP8 picture-descriptor visibility to the SFU.
  // - `kind` controls the libtgcalls audio-frame trailer (mandatory on Opus
  //   for the official client to accept our frames). Pass the actual
  //   `track.kind` of the sender/receiver. Defaults to 'audio' if omitted.
  // - 'recv' transforms dispatch per-frame: each encoded frame's
  //   `synchronizationSource` is looked up in the worker's SSRC → user_id
  //   table (populated via `setSsrcUsers`) to choose the verification key.
  //   No per-receiver `fromUserId` is needed — the same transform handles
  //   every SFU-multiplexed sender on its mid.
  public newRtcScriptTransform(options: {
    direction: 'send' | 'recv';
    channelId: number;
    unencryptedPrefixLength?: number;
    // Media kind of the track this transform is attached to. Drives the
    // libtgcalls audio-frame trailer wrap/strip in the worker — see
    // encryptWorker.ts (appendAudioTrailer / stripAudioTrailer).
    // Video frames are passed through raw.
    kind?: 'audio' | 'video';
  }): RTCRtpScriptTransform {
    // RTCRtpScriptTransform may not be in older TS DOM libs; access via
    // globalThis to avoid build-target issues.
    const Ctor = (globalThis as unknown as {RTCRtpScriptTransform?: typeof RTCRtpScriptTransform}).RTCRtpScriptTransform;
    if(!Ctor) throw new Error('RTCRtpScriptTransform is not supported in this browser');
    return new Ctor(this.worker, options);
  }

  // Push the SSRC → user_id table to the worker. Call whenever the SFU
  // participant set changes — the recv transform consults this on every
  // inbound frame. Unknown SSRCs are dropped silently.
  public setSsrcUsers(entries: Array<[number, bigint]>): Promise<void> {
    return this.invoke('setSsrcUsers', {entries});
  }

  // ===== Internal =====

  private invoke<K extends RequestKind>(
    kind: K,
    args?: K extends 'createKey' | 'pullOutbound' | 'getStatus' | 'commitRejoinBlock' | 'destroy' ?
      undefined :
      Extract<HostRequest, {kind: K}> extends {args: infer A} ? A : undefined
  ): Promise<RequestResultMap[K]> {
    if(this.failed && kind !== 'destroy') {
      return Promise.reject(new Error('EncryptWorkerHost: failed'));
    }
    if(this.destroyed) {
      return Promise.reject(new Error('EncryptWorkerHost: terminated'));
    }
    if(this.terminating && kind !== 'destroy') {
      return Promise.reject(new Error('EncryptWorkerHost: terminating'));
    }
    const id = this.nextId++;
    return new Promise<RequestResultMap[K]>((resolve, reject) => {
      this.pending.set(id, {
        resolve: (v: unknown) => resolve(v as RequestResultMap[K]),
        reject
      });
      const req = {kind, id, ...(args !== undefined ? {args} : {})} as HostRequest;
      try {
        this.worker.postMessage(req);
      } catch(err) {
        this.pending.delete(id);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  private async terminateInternal(): Promise<void> {
    try {
      await this.invokeDestroyWithTimeout();
    } catch{
      // Best-effort explicit wipe. The finally block always reclaims the worker
      // thread even when destroy rejects or never reaches the head of its queue.
    } finally {
      this.destroyed = true;
      this.worker.removeEventListener('message', this.onMessage);
      this.worker.removeEventListener('error', this.onError);
      this.worker.terminate();
      this.rejectPending(new Error('Worker terminated'));
      super.cleanup();
    }
  }

  private invokeDestroyWithTimeout(): Promise<void> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if(settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(finish, DESTROY_TIMEOUT_MS);
      this.invoke('destroy').then(finish, finish);
    });
  }

  private rejectPending(error: Error): void {
    for(const {reject} of this.pending.values()) reject(error);
    this.pending.clear();
  }

  private onMessage = (ev: MessageEvent<HostResponse>) => {
    if(this.destroyed) return;
    const msg = ev.data;
    if(msg.kind === 'event') {
      if(this.failed) return;
      // @ts-ignore - dispatchEvent's signature is too strict for our union.
      this.dispatchEvent(msg.event.kind, msg.event);
      return;
    }
    const pending = this.pending.get(msg.id);
    if(!pending) return;
    this.pending.delete(msg.id);
    if(msg.kind === 'ok') pending.resolve(msg.result);
    else pending.reject(new Error(msg.message));
  };

  private onError = (ev: ErrorEvent) => {
    // A worker-level error means the crypto runtime itself is no longer a
    // trustworthy RPC endpoint, even when no request happened to be pending.
    // Fail exactly once, stop accepting work and route the call through the
    // same fail-closed recovery path as an explicit worker `callFailed` event.
    if(this.failed || this.terminating || this.destroyed) return;

    this.failed = true;
    const message = ev.message || 'Encryption worker failed';
    const error = new Error(message);
    this.rejectPending(error);

    this.dispatchEvent('callFailed', {kind: 'callFailed', message});
    void this.terminate();
  };
}
